<template>
  <ContextColorProvider context="brand">
    <div class="global-allocation">
      <NavBar class="global-allocation__navbar">
        <template #left>
          <ButtonIconContextual
            icon="EF0030"
            label="Voltar"
            @click="onClickBack"
          />
        </template>
        <template #center>Alocação</template>
        <template #right>
          <ShowMoneyToggleButton />
        </template>
      </NavBar>
      <main>
        <div
          v-if="isLoading"
          data-testid="global-allocation-loader"
          class="global-allocation__loader"
        >
          <SpinnerLoader :size="48" />
        </div>
        <div v-else-if="hasError" class="global-allocation__error">
          <HelperViewStatus
            generic-error
            button-label="Tentar novamente"
            @click="getAllocationsByProduct"
          />
        </div>
        <TwoColumnsContainer v-else>
          <template #main>
            <section class="global-allocation__chart-section">
              <DonutChart
                class="global-allocation__chart"
                show-zero-values
                :data="productsAllocationChartData"
                :has-label="false"
                show-total-allocation
                :total-allocation="totalAllocation"
              />
              <BaseText
                :color="NebraskaColors.textSecondary"
                class="global-allocation__update"
                size="md"
              >
                Atualizado em {{ updatedAt }}
              </BaseText>
            </section>
          </template>
          <template #content>
            <GlobalAllocationProductList
              v-for="(product, index) in allocationProducts"
              :key="index"
              :product="product"
            />
          </template>
        </TwoColumnsContainer>
      </main>
    </div>
  </ContextColorProvider>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import {
  NavBar,
  ButtonIconContextual,
  SpinnerLoader,
  HelperViewStatus,
  BaseText,
  ContextColorProvider
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { TwoColumnsContainer } from '@/modules/common/components/TwoColumnsContainer';
import { DonutChart } from '@/modules/common/components/charts/DonutChart';
import { GlobalAllocationProductList } from './components/GlobalAllocationProductList';
import {
  getAllocationsByProduct,
  IAllocationsByProductResponse
} from '@/modules/products/services/investments';
import { useDashStore } from '@/modules/common/store/DashModule';
import { ShowMoneyToggleButton } from '@/modules/common/components/buttons/ShowMoneyToggleButton';

@Component({
  components: {
    ContextColorProvider,
    NavBar,
    ButtonIconContextual,
    SpinnerLoader,
    HelperViewStatus,
    TwoColumnsContainer,
    DonutChart,
    BaseText,
    GlobalAllocationProductList,
    ShowMoneyToggleButton
  }
})
export default class GlobalAllocationView extends Vue {
  readonly NebraskaColors = NebraskaColors;
  readonly dashModule = useDashStore();
  productAllocations: IAllocationsByProductResponse | null = null;
  isLoading = false;
  hasError = false;

  get productsAllocationChartData() {
    return this.allocationProducts.map(product => ({
      label: product.investmentClassType.formatted,
      value: product.allocPercentage.value,
      color: product.typeColor
    }));
  }

  get allocationProducts() {
    return this.productAllocations?.products ?? [];
  }

  get updatedAt() {
    return this.productAllocations?.balances.lastModifiedAtDate.formatted ?? '';
  }

  get totalAllocation() {
    return this.productAllocations?.balances.totalBalance.formatted ?? '';
  }

  async created() {
    await this.getAllocationsByProduct();
  }

  async getAllocationsByProduct() {
    this.hasError = false;
    this.isLoading = true;
    try {
      this.productAllocations = await getAllocationsByProduct();
    } catch (e) {
      console.error(e);
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  onClickBack() {
    this.$router.back();
  }
}
</script>

<style lang="less" scoped>
@navbar-height: 64px;
.global-allocation {
  &__navbar {
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 10;
  }
  &__loader {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: calc(100vh - @navbar-height);
  }
  &__error {
    height: calc(100vh - @navbar-height);
    padding: 0 20%;
    display: flex;
    align-items: center;
    justify-content: center;
    .helper-view {
      max-width: 320px;
    }
  }
  &__chart {
    max-width: calc(100vh - 45%);
    margin: 0 auto @size-spacing-x500;
  }
  &__update {
    width: 100%;
    text-align: center;
  }
}
</style>
